import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Toolbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowBack, ArrowDropDown, AutoAwesome, Comment, History } from "@mui/icons-material";
import {
  Group as PanelGroup,
  Panel,
  Separator as PanelResizeHandle,
} from "react-resizable-panels";
import axios from "axios";

import AttachmentList, { Attachment } from "components/AttachmentList";
import AlbumGallery from "components/AlbumGallery";
import { AlbumImage } from "model/albumModel";
import CommentsSection, { CompleteComment } from "components/CommentsSection";
import HistoriesSection, { HistoryEntry } from "components/HistoriesSection";
import FieldsList from "components/FieldsList";
import UserDataFieldsInfo from "components/UserDataFieldsInfo";
import Labels from "components/labels/Labels";
import { LabelData } from "components/labels/label_Interface";
import { RichTextEditor } from "components/Editor/plugins/RichTextEditor";

import { buildSharedApiUrl, buildSharedApiUrlWithHelpers } from "common/shareUrl";
import { getFileExtension } from "common/utilities";
import { getTranslation } from "common";
import { useIsMobile, useTranslations, useUser } from "hooks";
import Logo from "image/Logo.svg";

/**
 * Pagina pubblica (senza login) di un task condiviso: `/#/tasks/shared/<slug>`.
 *
 * Rende lo **snapshot congelato** che il proprietario ha selezionato
 * (`TaskShareLinkDialog`): niente vista live, niente azioni. Il payload arriva
 * da `GET /tasks/shared/<hash>` del backend statistiche, che legge solo
 * `processtokens_share`.
 *
 * I blocchi sono **gli stessi componenti del `TaskDetailDialog`** — `Paper` e
 * `Accordion` con la stessa struttura, `FieldsList`, `RichTextEditor`,
 * `AttachmentList`, `CommentsSection`, `Labels` — e il contenuto è nei due
 * pannelli ridimensionabili come nel dialog (`PanelGroup`, 60/40), così la resa
 * è identica invece di somigliante. La sola differenza è il contenitore
 * (una pagina con header, non una Dialog) e l'assenza di ogni azione.
 *
 * Il fetch usa axios grezzo, SENZA gli interceptor dell'app: l'`errorInterceptor`
 * fa scattare il logout globale su ogni 401/404 e qui un 404 è la norma (link
 * revocato). Stesso motivo per cui non si usa `taskShareApi`. Vedi
 * SharedChatPage, stesso schema.
 */

type SharedTaskSnapshot = {
  task: Record<string, any>;
  created_at: string;
  updated_at?: string;
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; data: SharedTaskSnapshot; apiUrl: string }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

const SHARE_PATH = "tasks/shared";

const STATE_LABELS: Record<number, { key: string; fallback: string }> = {
  0: { key: "pm.label.process_status.todo", fallback: "Da fare" },
  1: { key: "pm.label.process_status.doing", fallback: "In corso" },
  2: { key: "pm.label.process_status.done", fallback: "Completato" },
};

/** Estensioni per cui vale la pena chiedere l'anteprima immagine ad AttachmentList. */
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

const formatTimestamp = (iso?: string | null): string => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
  } catch {
    return "";
  }
};

const SharedTaskPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const translations = useTranslations();
  const isMobile = useIsMobile();
  // `undefined`/`null` = visitatore esterno (nessun utente in sessione): è il
  // caso per cui è pensato il disclaimer. Un utente loggato che apre il link
  // vede la pagina senza banner.
  const user = useUser();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [activeView, setActiveView] = useState<"comments" | "histories">("comments");

  // `getTranslation` ritorna la chiave se manca, quindi `|| fallback` non
  // scatterebbe mai: qui il fallback è vero.
  const t = (key: string, fallback: string) => translations?.[key] || fallback;

  // Il BE pubblico può vivere sulla radice dell'host (…/tasks/shared/<hash>)
  // oppure sotto il base path `/helpers`: si prova il primo e si ricade sul
  // secondo al 404. Quale dei due ha risposto lo salvo nello stato, così gli
  // allegati si scaricano dallo STESSO base (vedi `apiUrl`).
  const sharedUrl = useMemo(() => buildSharedApiUrl(slug || "", SHARE_PATH), [slug]);
  const sharedUrlWithHelpers = useMemo(
    () => buildSharedApiUrlWithHelpers(slug || "", SHARE_PATH),
    [slug]
  );

  useEffect(() => {
    if (!slug) {
      setState({ kind: "not_found" });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });

    const fetchSnapshot = (url: string, fallbackUrl?: string) => {
      axios
        .get<{ data: SharedTaskSnapshot }>(url, {
          headers: { "Accept-Language": navigator.language.split("-")[0] },
        })
        .then((res) => {
          if (cancelled) return;
          const payload = res.data?.data;
          if (!payload || typeof payload.task !== "object" || payload.task === null) {
            setState({ kind: "not_found" });
            return;
          }
          setState({ kind: "ok", data: payload, apiUrl: url });
          const name =
            (payload.task.flownodename as string) ||
            (payload.task.processmodelname as string) ||
            "Task";
          document.title = `Flowenti — ${name}`;
        })
        .catch((err) => {
          if (cancelled) return;
          const status = err?.response?.status;
          if (status === 404 && fallbackUrl && url !== fallbackUrl) {
            fetchSnapshot(fallbackUrl);
            return;
          }
          if (status === 404 || status === 403) {
            setState({ kind: "not_found" });
            return;
          }
          setState({
            kind: "error",
            message:
              err?.response?.data?.detail ||
              err?.message ||
              t("task_share.failed", "Impossibile recuperare il task condiviso."),
          });
        });
    };

    fetchSnapshot(sharedUrl, sharedUrlWithHelpers);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedUrl, sharedUrlWithHelpers, slug]);

  const task = state.kind === "ok" ? state.data.task : {};

  /** URL pubblico di un allegato dello snapshot: stessa base che ha servito il
   *  JSON, così non si sbaglia fra radice e `/helpers`. */
  const downloadUrlFor = (attachment: Attachment): string =>
    state.kind === "ok" ? `${state.apiUrl}/attachments/${attachment.id}` : "";

  // Lo snapshot congela le etichette già come {name, color}: niente idtag.
  const labels: LabelData[] = Array.isArray(task.labels)
    ? task.labels.map((l: any, i: number) => ({
        id: i,
        name: String(l?.name ?? ""),
        css: String(l?.color ?? ""),
      }))
    : [];

  const stateInfo =
    typeof task.state?.state === "number" ? STATE_LABELS[task.state.state] : undefined;

  const userdataEntries: Record<string, any> =
    task.userdata && typeof task.userdata === "object" ? task.userdata : {};

  const participants: any[] = Array.isArray(task.participants) ? task.participants : [];

  const attachments: Attachment[] = Array.isArray(task.attachments) ? task.attachments : [];
  // Album congelati nello snapshot: {name, images:[{id, caption, filename}]}.
  const albumsShown: {
    name: string;
    images: { id: number; caption: string; filename: string }[];
  }[] = Array.isArray(task.albums) ? (task.albums as any[]) : [];
  // Le immagini già dentro un album condiviso non si ripetono nell'accordion Allegati.
  const albumImageIds = new Set(
    albumsShown.flatMap((al) => (al.images ?? []).map((im) => im.id))
  );
  const attachmentsForList = attachments.filter((a) => !albumImageIds.has(a.id));
  const comments: CompleteComment[] = Array.isArray(task.comments) ? task.comments : [];
  const histories: HistoryEntry[] = Array.isArray(task.history) ? task.history : [];

  /** Anteprime: per le immagini si punta all'endpoint pubblico dell'allegato,
   *  che risponde con il content-type giusto — stessa resa del dialog, dove
   *  `images` contiene il base64. */
  const images: { [key: string]: string } = useMemo(() => {
    if (state.kind !== "ok") return {};
    const out: { [key: string]: string } = {};
    for (const a of attachments) {
      if (IMAGE_EXTENSIONS.includes((getFileExtension(a.filename) || "").toLowerCase())) {
        out[a.id] = `${state.apiUrl}/attachments/${a.id}`;
      }
    }
    return out;
  }, [attachments, state]);

  const dates: { key: string; fallback: string; value: string }[] =
    task.state && typeof task.state === "object"
      ? [
          { key: "task_share.date.created", fallback: "Creato", value: task.state.created },
          { key: "task_share.date.doing", fallback: "Preso in carico", value: task.state.startdoing },
          // La data di completamento si mostra solo se il task è davvero in
          // stato "done" (2): un `done` valorizzato su un task non chiuso è
          // rumore, non informazione.
          {
            key: "task_share.date.done",
            fallback: "Completato",
            value: task.state.state === 2 ? task.state.done : null,
          },
        ]
          .map((d) => ({ ...d, value: formatTimestamp(d.value) }))
          .filter((d) => d.value !== "")
      : [];

  /* ---------- blocchi, condivisi fra desktop (due pannelli) e mobile ---------- */

  const leftContent = (
    <Stack spacing={3}>
      {/* Nome processo — stessa struttura del dialog */}
      {task.description && (
        <Box>
          <Typography variant="h6" mb={2}>
            {t("pm.processstart.processname.label", "Nome processo")}
          </Typography>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              px: 1.5,
              py: 1,
            }}
          >
            <Typography variant="body1">{task.description}</Typography>
          </Box>
        </Box>
      )}

      {/* Descrizione dettagliata — lo stesso editor del dialog, in sola lettura */}
      {task.detail && (
        <Box>
          <Typography variant="h6" mb={2}>
            {t("pm.startprocess.description", "Descrizione dettagliata")}
          </Typography>
          <RichTextEditor
            value={String(task.detail)}
            readOnly
            minHeight={120}
            sx={{
              width: "100%",
              "& [class*='_root_']": { fontSize: "0.9375rem" },
              "& [contenteditable]": { fontSize: "0.9375rem" },
              "& [contenteditable] h1": { fontSize: "1.4rem" },
              "& [contenteditable] h2": { fontSize: "1.2rem" },
              "& [contenteditable] h3": { fontSize: "1.05rem" },
              "& [contenteditable] table": { fontSize: "0.9375rem" },
              "& .cm-editor": { fontSize: "0.9375rem" },
            }}
          />
        </Box>
      )}

      {/* Dati personalizzati + album + allegati: stessa Paper che li contiene nel dialog.
          L'album deve comparire anche se non si sono condivisi gli allegati (le sue
          immagini si servono comunque, autorizzate dallo snapshot). */}
      {(Object.keys(userdataEntries).length > 0 ||
        albumsShown.length > 0 ||
        attachments.length > 0) && (
        <Paper
          sx={{
            bgcolor: "background.paper",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
          elevation={0}
        >
          {Object.keys(userdataEntries).length > 0 && (
            <Accordion
              elevation={0}
              disableGutters
              defaultExpanded
              sx={accordionSx}
            >
              <AccordionSummary expandIcon={<ArrowDropDown />}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                    {t("generic.user_data.caption", "Dati personalizzati")}
                  </Typography>
                  <UserDataFieldsInfo stopPropagation />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <FieldsList
                  canManage={false}
                  schema={task.userschema}
                  // Nel dialog è la chiave localStorage di comodo: qui non serve
                  // (e idprocess non entra nello snapshot di proposito).
                  selectedProcess={undefined}
                  value={userdataEntries}
                  onChange={() => {}}
                  openAddFieldDialog={false}
                  onAddFieldDialogClose={() => {}}
                  handleSaveUserdata={async () => {}}
                  readonly
                />
              </AccordionDetails>
            </Accordion>
          )}

          {albumsShown.length > 0 && (
            <Accordion elevation={0} disableGutters defaultExpanded sx={accordionSx}>
              <AccordionSummary expandIcon={<ArrowDropDown />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                  {getTranslation(translations, "album.label")} ({albumsShown.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ mt: -2 }}>
                <Stack spacing={3}>
                  {albumsShown.map((al, i) => (
                    <Box key={`${al.name}-${i}`}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {al.name}
                      </Typography>
                      <AlbumGallery
                        items={al.images.map(
                          (im) => ({ idupload: im.id, caption: im.caption } as AlbumImage)
                        )}
                        thumbFor={(id) =>
                          `${state.kind === "ok" ? state.apiUrl : ""}/attachments/${id}`
                        }
                        labelFor={(id) => al.images.find((im) => im.id === id)?.filename ?? ""}
                      />
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {attachmentsForList.length > 0 && (
            <Accordion elevation={0} disableGutters defaultExpanded sx={accordionSx}>
              <AccordionSummary expandIcon={<ArrowDropDown />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                  {t("settings.attachments.label", "Allegati")} ({attachmentsForList.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ mt: -3 }}>
                <AttachmentList
                  canManage={false}
                  attachments={attachmentsForList}
                  knownWidth={0}
                  getAttachments={async () => {}}
                  images={images}
                  histories={{}}
                  downloadUrlFor={downloadUrlFor}
                />
              </AccordionDetails>
            </Accordion>
          )}
        </Paper>
      )}

      {/* Persone coinvolte: nel dialog non c'è un blocco equivalente, qui sta
          nella colonna del contenuto con la stessa tipografia. */}
      {participants.length > 0 && (
        <Box>
          <Typography variant="h6" mb={2}>
            {t("generic.participants", "Persone coinvolte")}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {participants.map((p, i) => (
              <Chip
                key={`${p?.name}-${i}`}
                size="small"
                variant="outlined"
                label={String(p?.name ?? "")}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );

  const rightPanelContent = (
    <Paper
      sx={{
        width: "100%",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
      }}
      elevation={0}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
            {activeView === "comments"
              ? t("settings.comments.label", "Commenti")
              : t("settings.histories.label", "Storico")}
          </Typography>
          {/* Lo stesso toggle Commenti/Storico del TaskDetailDialog. */}
          <ToggleButtonGroup
            value={activeView}
            exclusive
            onChange={(_, newView) => {
              if (newView !== null) setActiveView(newView);
            }}
            size="small"
            aria-label="view selection"
            sx={{
              "& .MuiToggleButton-root": {
                color: "primary.main",
                borderColor: "divider",
                "&:hover": { color: "primary.light", bgcolor: "action.hover" },
                "&.Mui-selected": { color: "primary.main", bgcolor: "action.selected" },
                "&.Mui-selected:hover": { bgcolor: "action.hover" },
              },
            }}
          >
            <Tooltip title={t("settings.comments.label", "Commenti")}>
              <ToggleButton value="comments" aria-label="comments">
                <Comment color="inherit" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("settings.histories.label", "Storico")}>
              <ToggleButton value="histories" aria-label="histories">
                <History color="inherit" />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Stack>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: 2 }}>
        {activeView === "comments" ? (
          /* Nessuna scrittura, nessuna modifica: è la stessa sezione del dialog
             con i permessi spenti. */
          <CommentsSection canWrite={false} canEdit={false} comments={comments} />
        ) : (
          <HistoriesSection history={histories} />
        )}
      </Box>
    </Paper>
  );

  const header = (
    <>
      {task.processmodelname && (
        <Typography variant="overline" color="text.secondary">
          {task.processmodelname}
        </Typography>
      )}
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        {task.flownodename || task.processmodelname || "Task"}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        {stateInfo && (
          <Chip
            size="small"
            label={t(stateInfo.key, stateInfo.fallback)}
            color={
              task.state.state === 2 ? "success" : task.state.state === 1 ? "primary" : "default"
            }
          />
        )}
        {task.priority && (
          <Chip size="small" variant="outlined" label={String(task.priority)} />
        )}
        {labels.length > 0 && <Labels labelCompleteMode labels={labels} />}
        <Typography variant="caption" color="text.secondary">
          {t("task_share.shared_on", "Condiviso il")}{" "}
          {formatTimestamp(state.kind === "ok" ? state.data.created_at : "")}
        </Typography>
      </Stack>
      {dates.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {dates.map((d) => `${t(d.key, d.fallback)}: ${d.value}`).join(" · ")}
        </Typography>
      )}
      <Divider sx={{ mt: 2 }} />
    </>
  );

  return (
    <Box
      sx={{
        // Altezza fissa: i due pannelli devono avere un'area che scorre
        // internamente, come dentro la Dialog del task detail.
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? theme.palette.background.default : "#f5f5f7",
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : "rgba(255, 255, 255, 0.95)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Stack
            component={RouterLink}
            to="/"
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ textDecoration: "none", color: "inherit" }}
          >
            <img src={Logo} alt="Flowenti" style={{ height: 36 }} />
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              Flowenti
            </Typography>
          </Stack>
          <Box sx={{ flex: 1 }} />
          <Button
            component={RouterLink}
            to="/"
            size="small"
            startIcon={<ArrowBack fontSize="small" />}
            sx={{ borderRadius: 999, textTransform: "none", color: "text.secondary" }}
          >
            {t("generic.home", "Home")}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Disclaimer per i visitatori esterni: chi non ha un account vede un
          invito a iscriversi, con la CTA che porta alla home pubblica (`/` →
          LandingPage per un utente anonimo). Per un utente loggato non compare. */}
      {!user && (
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            color: "common.white",
            background: (theme) =>
              `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 100%)`,
          }}
        >
          <Container maxWidth="xl" disableGutters>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1.5, sm: 3 }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AutoAwesome fontSize="small" sx={{ opacity: 0.9 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                    {t(
                      "task_share.signup.title",
                      "Stai consultando un task condiviso con Flowenti"
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {t(
                      "task_share.signup.body",
                      "Flowenti organizza processi, attività e team in un unico posto. Crea il tuo account gratuito in pochi secondi."
                    )}
                  </Typography>
                </Box>
              </Stack>
              <Button
                component={RouterLink}
                to="/"
                variant="contained"
                size="large"
                sx={{
                  flexShrink: 0,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  bgcolor: "common.white",
                  color: "primary.main",
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                {t("task_share.signup.cta", "Iscriviti ora!")}
              </Button>
            </Stack>
          </Container>
        </Box>
      )}

      {/* Area contenuto: `minHeight: 0` è quello che permette ai pannelli di
          scorrere invece di far crescere la pagina. */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {state.kind === "loading" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flex: 1,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              {t("generic.loading", "Caricamento...")}
            </Typography>
          </Box>
        )}

        {state.kind === "not_found" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flex: 1,
              textAlign: "center",
            }}
          >
            <Alert severity="warning" sx={{ maxWidth: 480 }}>
              {t(
                "task_share.not_found",
                "Questo task condiviso non è più disponibile o il link non è valido."
              )}
            </Alert>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              sx={{ borderRadius: 999, textTransform: "none" }}
            >
              {t("generic.home", "Torna alla home")}
            </Button>
          </Box>
        )}

        {state.kind === "error" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flex: 1,
              textAlign: "center",
            }}
          >
            <Alert severity="error" sx={{ maxWidth: 480 }}>
              {state.message}
            </Alert>
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
              sx={{ borderRadius: 999, textTransform: "none" }}
            >
              {t("generic.retry", "Riprova")}
            </Button>
          </Box>
        )}

        {state.kind === "ok" &&
          (isMobile ? (
            <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 3 }}>
              <Box sx={{ mb: 3 }}>{header}</Box>
              {leftContent}
              <Box sx={{ mt: 4, height: 480 }}>{rightPanelContent}</Box>
            </Box>
          ) : (
            <Container maxWidth="xl" sx={{ flex: 1, minHeight: 0, px: 3, py: 3 }}>
              <Box sx={{ mb: 3 }}>{header}</Box>
              <Box sx={{ height: "calc(100% - 140px)", minHeight: 360 }}>
                <PanelGroup
                  orientation="horizontal"
                  style={{ overflow: "hidden", height: "100%" }}
                >
                  {/* Pannello sinistro: contenuto — stesse misure del dialog */}
                  <Panel
                    id="shared-task-left"
                    defaultSize="60%"
                    minSize="35%"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      overflow: "auto",
                      padding: "0 12px 0 24px",
                    }}
                  >
                    {leftContent}
                  </Panel>

                  <PanelResizeHandle className="panel-resize-handle" style={handleStyle}>
                    <Box sx={handleInnerSx} />
                  </PanelResizeHandle>

                  {/* Pannello destro: commenti */}
                  <Panel
                    id="shared-task-right"
                    defaultSize="40%"
                    minSize="25%"
                    style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
                  >
                    {rightPanelContent}
                  </Panel>
                </PanelGroup>
              </Box>
            </Container>
          ))}
      </Box>

      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: "center",
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? theme.palette.background.paper : "#f8f9fa",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t("task_share.footer", "Task condiviso tramite Flowenti")}
        </Typography>
      </Box>
    </Box>
  );
};

/** Stessi stili degli Accordion del task detail (nessuna riga di separazione). */
const accordionSx = {
  "&:before": { display: "none" },
  boxShadow: "none",
  "&.MuiAccordion-root": { border: "none" },
  "&.Mui-expanded": { margin: 0 },
} as const;

/** Maniglia di ridimensionamento: copiata dal dialog, così non ci sono
 *  differenze visive fra i due. */
const handleStyle: React.CSSProperties = {
  width: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "col-resize",
  transition: "background 0.15s",
};

const handleInnerSx = {
  width: 6,
  height: 38,
  borderRadius: 4,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  backgroundColor: (theme: any) =>
    theme.palette.mode === "dark" ? "#ffffff" : "action.hover",
  py: 0.75,
  transition: "background-color 0.15s",
} as const;

export default SharedTaskPage;
